import { useQuery } from '@tanstack/react-query'
import { api } from '@astro/api'
import { TextInput, BadgeGroup } from '../fields'
import SelectField from '../SelectField'
import { SESSION_DURATIONS, FALLBACK_SKILLS, FALLBACK_LANGUAGES } from '../registerSteps'

/* `showRate` lets profile-mode hosts hide the session-rate field for
   specific accounts; the register wizard always shows it (default true).
   Hidden ≠ cleared: the prefetched value stays in form state, so the
   profile-update payload still carries the account's existing rate. */
export default function StepProfessional({ showRate = true }) {
  // Live reference lists (they rarely change); fall back to static options so
  // the wizard still works if the lookups are unreachable.
  const { data: skills } = useQuery({
    queryKey: ['skills'],
    queryFn: () => api.lookups.skills(),
    staleTime: Infinity,
    retry: false,
  })
  const { data: languages } = useQuery({
    queryKey: ['languages'],
    queryFn: () => api.lookups.languages(),
    staleTime: Infinity,
    retry: false,
  })

  // Badge values carry the lookup IDs the payload wants; fallbacks keep names.
  const skillOptions = skills?.length
    ? skills.map((s) => ({ value: String(s.id), label: s.name }))
    : FALLBACK_SKILLS
  const languageOptions = languages?.length
    ? languages.map((l) => ({ value: String(l.id), label: l.name }))
    : FALLBACK_LANGUAGES

  return (
    <div className="step-panel space-y-4">
      {/* Experience + Duration share the first row; Rate sits on its own row
          below so hiding/unhiding it never disturbs the layout. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextInput
          name="experience"
          type="number"
          label="Experience (Years)"
          placeholder="10"
          min={0}
          rules={{
            required: 'Experience is required.',
            min: { value: 0, message: 'Experience cannot be negative.' },
          }}
        />
        <SelectField
          name="duration"
          label="Session Duration"
          options={SESSION_DURATIONS.map((minutes) => ({ value: String(minutes), label: `${minutes} min` }))}
          placeholder="Select duration"
          rules={{ required: 'Select a duration.' }}
        />
      </div>
      {showRate && (
        <TextInput
          name="rate"
          type="number"
          label="Rate per Session (₹)"
          placeholder="999"
          min={0}
          rules={{
            required: 'Session rate is required.',
            min: { value: 0, message: 'Rate cannot be negative.' },
          }}
        />
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <BadgeGroup
          name="languages"
          label="Languages Known"
          options={languageOptions}
          rules={{ validate: (v) => (v && v.length > 0) || 'Select at least one language.' }}
        />
        <BadgeGroup
          name="skills"
          label="Areas of Expertise / Skills"
          options={skillOptions}
          rules={{ validate: (v) => (v && v.length > 0) || 'Select at least one skill.' }}
        />
      </div>
    </div>
  )
}
